import React, { useState, useEffect } from 'react';
import styles from './EmailLoginPanel.module.css';
import * as api from '../../../api/Passwordless';

// Basic email shape check so we don't send "david" to the server and then show
// a misleading "we sent you a link" message.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Sign in with email" — the password alternative on the login screen. It has
// no own input: the email is taken from the main login email field. The button
// is shown only when the server reports the feature is enabled (no misleading
// UI). We email a one-time link bound to this browser, so it only works if
// opened on this same device.
export default function EmailLoginPanel() {
  const [enabled, setEnabled] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .emailConfig()
      .then((response) => {
        if (active) {
          setEnabled(Boolean(response.data.enabled));
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Read the email from the main login form's field (the shared Form keeps its
  // value internally, so read the live input rather than duplicating it).
  const readMainEmail = () => {
    const input = document.querySelector('input[type="email"]');
    return ((input && input.value) || '').trim();
  };

  const handleSend = async () => {
    const email = readMainEmail();
    if (!email) {
      setError('Enter your email above, then request a sign-in link.');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      await api.emailRequest(email);
      setSentTo(email);
    } catch (e) {
      const message =
        (e.response && e.response.data && e.response.data.message) ||
        'Something went wrong. Please try again.';
      setError(message);
    }
    setIsBusy(false);
  };

  // Hidden entirely unless the server says email sign-in is enabled.
  if (!enabled) {
    return null;
  }

  return (
    <div className={styles.EmailLoginPanel}>
      <div className={styles.Divider}>
        <span className={styles.DividerLabel}>or</span>
      </div>

      {sentTo ? (
        <div className={styles.Info}>
          If an account exists for <b>{sentTo}</b>, we sent a sign-in link to
          it. Open it <b>in this browser</b> to finish signing in - it expires
          soon and works only on this device.{' '}
          <button
            type="button"
            className={styles.LinkButton}
            onClick={handleSend}
            disabled={isBusy}
          >
            Resend
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.Button}
          onClick={handleSend}
          disabled={isBusy}
        >
          {isBusy ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      )}

      {error && <div className={styles.Error}>{error}</div>}
    </div>
  );
}
